import React, { useEffect, useState } from 'react';
import TaskActivityView from '../view/TaskActivityView';
import fetchAPI from '../../../utils/fetchAPI';
import {
  getActivityTimelineFriendlyDate,
  getFriendlyDueDate
} from '../../../utils/dateUtils';

const FIELD_ID_TO_DISPLAY = {
  priority: 'Priority',
  dueDate: 'Due date',
  status: 'Status',
  title: 'Title',
  description: 'Description',
  assigneeId: 'Assigned to'
};

const VALUE_TO_DISPLAY = {
  inProgress: 'In progress',
  assigned: 'Assigned',
  complete: 'Complete',
  low: 'Low',
  medium: 'Medium',
  high: 'High'
};

const TaskActivityEngine = ({
  isEndUser = false,
  taskId,
  timelineReadyCallback = () => {}
}) => {
  const [activityData, setActivityData] = useState([]);
  const [isReady, setIsReady] = useState(false);

  const getUserInfoByIdsPromise = ids => {
    return new Promise((resolve, reject) => {
      const VINCI_SUPPORT_ID = 'f2618836-2355-496c-b245-1c1ae2258101';
      if (!ids.length) {
        resolve({});
      }
      const usersQuery = `/users/v1/eu/search?query=[[${ids
        .map(
          one =>
            `{"field": "'id'", "op": "=", "value": "${one}", "internOp": "OR"}`
        )
        .join(',')}]]`;
      fetchAPI(usersQuery).then(response => {
        const userResults = response.results || [];
        let resultsById = {};
        userResults.forEach(one => {
          if (!one.info) {
            one.info = {};
          }
          one.info.fullName = one?.info?.firstName + ' ' + one?.info?.lastName;
          resultsById[one.id] = one;
        });
        if (ids.includes(VINCI_SUPPORT_ID) && !resultsById[VINCI_SUPPORT_ID]) {
          resultsById[VINCI_SUPPORT_ID] = {
            id: VINCI_SUPPORT_ID,
            info: {
              email: 'vinci_support@vinciworks.com',
              firstName: 'Vinci',
              lastName: 'Support',
              fullName: 'Vinci Support'
            }
          };
        }
        resolve(resultsById);
      });
    });
  };

  const getTimelineData = () => {
    const timeLineURL = `/tasks/v1/${
      isEndUser ? 'eu/' : ''
    }tasks/${taskId}/timeline`;
    fetchAPI(timeLineURL).then(timelineRes => {
      const userIds = timelineRes.map(one => one.updated_by_user_id);
      const changedUserIds = timelineRes
        .filter(
          one =>
            one.event === 'value_update' &&
            one.field_changed_name === 'assigneeId'
        )
        .map(one => one.new_value);
      const uniqueIds = [...new Set([...userIds, ...changedUserIds])];
      getUserInfoByIdsPromise(uniqueIds).then(userRes => {
        let newActivityData = [],
          newActivityRow = null;
        timelineRes.forEach(one => {
          switch (one.event) {
            case 'task_created':
              newActivityRow = (
                <span>
                  <strong>Task created</strong> by{' '}
                  {userRes[one.updated_by_user_id]?.info?.fullName ||
                    one.updated_by_user_id}
                </span>
              );
              break;
            case 'value_update':
              newActivityRow = (
                <span>
                  <strong>
                    {FIELD_ID_TO_DISPLAY[one.field_changed_name] ||
                      one.field_changed_name}
                  </strong>{' '}
                  change to{' '}
                  <strong>
                    {one.field_changed_name === 'assigneeId'
                      ? userRes[one.new_value]?.info?.fullName || one.new_value
                      : one.field_changed_name === 'dueDate'
                      ? one.new_value
                        ? getFriendlyDueDate(one.new_value)
                        : one.new_value
                      : VALUE_TO_DISPLAY[one.new_value] || one.new_value}
                  </strong>{' '}
                  by{' '}
                  {userRes[one.updated_by_user_id]?.info?.fullName ||
                    one.updated_by_user_id}
                </span>
              );
              break;
            case 'comment':
              newActivityRow = (
                <>
                  <div>
                    <strong>
                      Comment by{' '}
                      {userRes[one.updated_by_user_id]?.info?.fullName ||
                        one.updated_by_user_id}
                    </strong>
                  </div>
                  <div>
                    <i>"{one.new_value}"</i>
                  </div>
                </>
              );
              break;
            case 'file_uploaded':
              newActivityRow = (
                <>
                  <div>
                    <strong>Attachment uploaded</strong> by{' '}
                    {userRes[one.updated_by_user_id]?.info?.fullName ||
                      one.updated_by_user_id}
                  </div>
                  <div>
                    <i>{decodeURIComponent(one.field_changed_name)}</i>
                  </div>
                </>
              );
              break;
            case 'file_removed':
              newActivityRow = (
                <>
                  <div>
                    <strong>Attachment deleted</strong> by{' '}
                    {userRes[one.updated_by_user_id]?.info?.fullName ||
                      one.updated_by_user_id}
                  </div>
                  <div>
                    <i>{one.field_changed_name}</i>
                  </div>
                </>
              );
              break;
            default:
              break;
          }
          if (newActivityRow) {
            newActivityData.push({
              formattedDate: getActivityTimelineFriendlyDate(one.date),
              activityRow: newActivityRow,
              event: one.event
            });
          }
        });
        setActivityData(newActivityData);
        setIsReady(true);
      });
    });
  };

  useEffect(() => {
    getTimelineData();
  }, []);

  useEffect(() => {
    if (isReady) {
      timelineReadyCallback();
    }
  }, [isReady]);

  const viewProps = {
    activityData
  };

  return <TaskActivityView {...viewProps} />;
};

export default TaskActivityEngine;
